// web/src/components/mic.ts — One getUserMedia per session (UI-07).
//
// Pitfall #2 mitigation: stream is acquired exactly once after the
// preflight tap (D-14) and held until /finish or window unload.
// Releasing tracks revokes the iOS implicit grant and forces the
// OS sheet on the next acquire — UI-07 forbids that.
//
// The AnalyserNode is created once alongside the stream and reused
// across turns. waveform.ts pauses by stopping its rAF loop, NOT by
// disconnecting the AnalyserNode.

const PREFERRED_MIMES = [
  "audio/webm;codecs=opus",   // Chrome / Firefox / Edge
  "audio/mp4",                // Safari iOS 14+
  "audio/ogg;codecs=opus",    // some Firefox builds
  "audio/wav",                // last resort
];

// TARGET_SAMPLE_RATE feeds BOTH streaming providers: OpenAI Realtime requires
// pcm16/24kHz; Deepgram accepts linear16/24kHz. We request a 24kHz AudioContext
// so the browser resamples the mic for us; the PCM worklet resamples in JS only
// if the browser ignored the hint (older Safari/iOS). RMS/analyser is
// rate-agnostic, so the silence watcher is unaffected.
const TARGET_SAMPLE_RATE = 24000;

let stream: MediaStream | null = null;
let chosenMime: string | null = null;
let audioCtx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let micSource: MediaStreamAudioSourceNode | null = null;

export async function acquireMic(): Promise<{ stream: MediaStream; mime: string; analyser: AnalyserNode }> {
  if (stream && chosenMime !== null && analyser) {
    return { stream, mime: chosenMime, analyser };
  }
  stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  for (const m of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(m)) {
      chosenMime = m;
      break;
    }
  }
  if (chosenMime === null) chosenMime = "";  // browser-default fallback

  // Request a 24kHz context (honored by modern Chrome/Firefox/Safari). Read
  // back the actual rate — the PCM worklet uses it to decide whether to
  // resample. A browser that rejects the option throws; fall back to default.
  try {
    audioCtx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
  } catch {
    audioCtx = new AudioContext();
  }
  analyser = audioCtx.createAnalyser();
  analyser.fftSize = 1024;  // OQ-10: 1024 is cheap on low-end Android
  micSource = audioCtx.createMediaStreamSource(stream);
  micSource.connect(analyser);

  return { stream, mime: chosenMime, analyser };
}

// getAudioContext / getMicSource expose the shared capture graph so the PCM
// streaming worklet can tap the SAME MediaStreamAudioSourceNode the analyser
// uses (fan-out from one source is supported). Returns null before acquireMic.
export function getAudioContext(): AudioContext | null { return audioCtx; }
export function getMicSource(): MediaStreamAudioSourceNode | null { return micSource; }

// getSampleRate returns the actual AudioContext sample rate (may differ from
// the requested 24kHz on browsers that ignore the hint). 0 before acquireMic.
export function getSampleRate(): number { return audioCtx ? audioCtx.sampleRate : 0; }

// TargetSampleRate is the PCM16 rate both streaming providers expect.
export const TargetSampleRate = TARGET_SAMPLE_RATE;

export function newRecorder(): MediaRecorder {
  if (!stream) throw new Error("mic.newRecorder: stream not acquired — call acquireMic first");
  return chosenMime
    ? new MediaRecorder(stream, { mimeType: chosenMime })
    : new MediaRecorder(stream);
}

export function getMime(): string {
  if (chosenMime === null) throw new Error("mic.getMime: not acquired");
  return chosenMime;
}

export async function releaseMic(): Promise<void> {
  if (stream) {
    for (const t of stream.getTracks()) t.stop();
    stream = null;
  }
  chosenMime = null;
  analyser = null;
  micSource = null;
  if (audioCtx) {
    await audioCtx.close();
    audioCtx = null;
  }
}

// For tests / introspection only — confirms the singleton is held.
export function isAcquired(): boolean { return stream !== null; }
